import { useState, useCallback, useEffect, useMemo } from 'react';

export interface TableColumn {
  id: string;
  title: string;
  width: string;
  visible: boolean;
  /** id комбо, в котором находится столбец, или null */
  inCombo: string | null;
}

export interface ComboConfig {
  childIds: string[];
  title: string;
}

const STORAGE_KEY_ORDER = 'project-table-order';
const STORAGE_KEY_COLUMNS = 'project-table-columns';
const STORAGE_KEY_PROJECTS_OLD = 'projects-table-columns';
const STORAGE_KEY_COMBOS_OLD = 'projects-table-combos';

/** Версия схемы таблицы проектов. Увеличивайте при любом изменении дефолтов столбцов, комбо, ширин в этом файле — у пользователей один раз сбросятся сохранённые order/columns/widths/combos. */
export const PROJECT_TABLE_LAYOUT_SCHEMA_VERSION = 13;
const STORAGE_KEY_SCHEMA_VERSION = 'project-table-layout-schema-version';

/** Ключи, которые сбрасываются при bump PROJECT_TABLE_LAYOUT_SCHEMA_VERSION (сама версия не удаляется — перезаписывается). */
const KEYS_TO_CLEAR_ON_SCHEMA_BUMP: readonly string[] = [
  STORAGE_KEY_ORDER,
  STORAGE_KEY_COLUMNS,
  STORAGE_KEY_PROJECTS_OLD,
  STORAGE_KEY_COMBOS_OLD,
  /** см. useTypeBadgeVisibility.ts */
  'project-table-type-badges',
  // устаревшие одноразовые флаги — чистим при смене схемы
  'project-table-performers-studio-split-v1',
  'project-table-performers-review-width-200-v1',
  /** см. useTableCompactMode.ts — сброс компактного режима чтец/контрагенты к дефолту */
  'project-table-compact-mode',
];

/** Очистка ключей localStorage при смене {@link PROJECT_TABLE_LAYOUT_SCHEMA_VERSION}. Безопасно вызывать повторно. */
export function applyProjectTableLayoutSchemaMigration(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const stored = localStorage.getItem(STORAGE_KEY_SCHEMA_VERSION);
    const current = String(PROJECT_TABLE_LAYOUT_SCHEMA_VERSION);
    if (stored === current) return;
    for (const key of KEYS_TO_CLEAR_ON_SCHEMA_BUMP) {
      localStorage.removeItem(key);
    }
    localStorage.setItem(STORAGE_KEY_SCHEMA_VERSION, current);
  } catch {
    // ignore
  }
}

/* Столбцы, которые нельзя объединять в комбо (state/dates всегда; чтец/контрагенты — только при выключенном компактном режиме) */
export function getNoComboColumnIds(compactReaders: boolean, compactStudio: boolean): Set<string> {
  const s = new Set<string>(['state', 'dates']);
  if (!compactReaders) s.add('readers');
  if (!compactStudio) s.add('studio');
  return s;
}

/** Статический набор при полностью выключенном компактном режиме (обратная совместимость импортов). */
export const NO_COMBO_COLUMN_IDS = getNoComboColumnIds(false, false);

/** Меню, заметки, сессии и файлы — объединяются только между собой */
export const MENU_COMBO_GROUP = new Set(['actions', 'notes', 'sessions', 'files']);

/** Фонограмма, ЦП, согласован, сдан, PDF, фонотека — кнопочные столбцы */
export const STATUS_BUTTON_GROUP = new Set([
  'project_phonogram',
  'project_dp',
  'project_approved',
  'project_done',
  'project_pdf',
  'has_music',
]);

/** Все кнопочные столбцы — можно объединять между собой (меню + статус-кнопки) */
export const BUTTON_COLUMNS_GROUP = new Set([...MENU_COMBO_GROUP, ...STATUS_BUTTON_GROUP]);

/* Столбцы, которые нельзя скрыть (не показываются в меню скрытия столбцов) */
export const NO_HIDE_IDS = new Set(['actions', 'name', 'notes', 'sessions', 'files']);

/** Названия для меню (заголовки столбцов остаются пустыми) */
export const MENU_COLUMN_LABELS: Record<string, string> = {
  actions: 'Действия',
  notes: 'Заметки',
  sessions: 'Сессии',
  files: 'Файлы',
  menu_combo: 'Заметки, Файлы, PDF, Фонотека',
  status_flags_combo: 'Фонограмма, ЦП, Согласовано, Проект сдан',
  project_phonogram: 'Фонограмма',
  project_dp: 'ЦП',
  project_approved: 'Согласовано',
  project_done: 'Проект сдан',
  project_pdf: 'PDF',
  has_music: 'Фонотека',
  kf_combo: 'КФ записи, КФ монтажа',
};

/* Предустановленные комбо по умолчанию */
export const DEFAULT_COMBOS: Record<string, ComboConfig> = {
  menu_combo: { childIds: ['files', 'notes', 'project_pdf', 'has_music'], title: '' },
  status_flags_combo: {
    childIds: ['project_phonogram', 'project_dp', 'project_approved', 'project_done'],
    title: '',
  },
  book_data: { childIds: ['asta', 'ase', 'rin'], title: 'Данные' },
  performers: { childIds: ['producer', 'techeditor', 'corrector'], title: 'Исполнители' },
  customers: { childIds: ['department', 'edition', 'editor'], title: 'Заказчики' },
  review_combo: {
    childIds: ['review_deadline', 'review_check_type', 'corrections_number'],
    title: 'Проверка',
  },
  kf_combo: { childIds: ['recording_coef', 'montage_coef'], title: 'КФ' },
};

/* Ширина узких иконочных столбцов (меню, заметки, сессии, файлы) */
const MENU_COLUMN_WIDTH = '44px';
/** Первая колонка «Действия» (контекстное меню строки) — с узкими числовыми колонками 50px */
const ACTIONS_MENU_COLUMN_WIDTH = '50px';

/* Все возможные столбцы */
const ALL_COLUMN_DEFS: Record<string, { title: string; width: string }> = {
  menu_combo: { title: '', width: '210px' },
  actions: { title: '', width: ACTIONS_MENU_COLUMN_WIDTH },
  notes: { title: '', width: MENU_COLUMN_WIDTH },
  sessions: { title: '', width: MENU_COLUMN_WIDTH },
  files: { title: '', width: MENU_COLUMN_WIDTH },
  state: { title: 'Состояние', width: '180px' },
  status_flags_combo: { title: '', width: '170px' },
  project_phonogram: { title: 'Фонограмма', width: MENU_COLUMN_WIDTH },
  project_dp: { title: 'ЦП', width: MENU_COLUMN_WIDTH },
  project_approved: { title: 'Согласовано', width: MENU_COLUMN_WIDTH },
  project_done: { title: 'Проект сдан', width: MENU_COLUMN_WIDTH },
  project_pdf: { title: 'PDF', width: MENU_COLUMN_WIDTH },
  has_music: { title: 'Фонотека', width: '76px' },
  project_type: { title: 'Тип', width: '56px' },
  book_data: { title: 'Данные', width: '160px' },
  asta: { title: 'ASTA', width: '160px' },
  ase: { title: 'ASE', width: '160px' },
  rin: { title: 'РИН', width: '80px' },
  producer: { title: 'Продюсер', width: '160px' },
  techeditor: { title: 'Техредактор', width: '160px' },
  corrector: { title: 'Корректор', width: '160px' },
  studio: { title: 'Контрагенты', width: '400px' },
  department: { title: 'Подразделение', width: '50px' },
  editor: { title: 'Редактор', width: '160px' },
  edition: { title: 'Редакция', width: '160px' },
  name: { title: 'Название', width: '320px' },
  author: { title: 'Автор', width: '180px' },
  readers: { title: 'Чтец', width: 'calc(400px + var(--space-3))' },
  author_sheets: { title: 'АЛ', width: '50px' },
  pages: { title: 'Страницы', width: '100px' },
  time: { title: 'Время', width: '100px' },
  kf_combo: { title: 'КФ', width: '120px' },
  recording_coef: { title: 'КФ записи', width: '50px' },
  montage_coef: { title: 'КФ монтажа', width: '50px' },
  deadline: { title: 'Дедлайн', width: '120px' },
  review_combo: { title: 'Проверка', width: '180px' },
  review_check_type: { title: 'Тип проверки', width: '130px' },
  review_deadline: { title: 'Дедлайн проверки', width: '120px' },
  corrections_number: { title: 'Кол-во правок', width: '72px' },
  rights: { title: 'Права', width: '60px' },
  performers: { title: 'Исполнители', width: '200px' },
  customers: { title: 'Заказчики', width: '200px' },
  theme: { title: 'Тематика', width: '160px' },
  paper_book_plan: { title: 'План бумаги', width: '120px' },
  /** 140 — «ПРОВЕРКА ПРАВОК» (≈76px) в одну строку рядом с датой (≈39px); расчёт — у .projects-table__dates-cell */
  dates: { title: 'Даты', width: '140px' },
};

/* Порядок по умолчанию: actions первым, status_flags_combo после state, menu_combo после deadline, исполнители сразу после названия */
const DEFAULT_COLUMN_ORDER: string[] = [
  'actions',
  'state',
  'status_flags_combo',
  'dates',
  'project_type',
  'name',
  'performers',
  'author',
  'readers',
  'author_sheets',
  'pages',
  'sessions',
  'time',
  'kf_combo',
  'deadline',
  'menu_combo',
  'review_combo',
  'studio',
  'customers',
  'paper_book_plan',
  'rights',
  'book_data',
  'theme',
];

/** Канонический порядок всех атомарных столбцов. Используется для порядка полей в комбо и в меню. */
export const PROJECT_TABLE_ORDER: string[] = (() => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of DEFAULT_COLUMN_ORDER) {
    const childIds = DEFAULT_COMBOS[id]?.childIds;
    if (childIds?.length) {
      for (const cid of childIds) {
        if (!seen.has(cid)) {
          seen.add(cid);
          result.push(cid);
        }
      }
    } else if (!DEFAULT_COMBOS[id] && !seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  }
  for (const id of Object.keys(ALL_COLUMN_DEFS)) {
    if (!DEFAULT_COMBOS[id] && !seen.has(id)) {
      result.push(id);
    }
  }
  return result;
})();

function getDefaultAtomicOrder(): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of DEFAULT_COLUMN_ORDER) {
    const childIds = DEFAULT_COMBOS[id]?.childIds;
    if (childIds?.length) {
      for (const cid of childIds) {
        if (!seen.has(cid)) {
          seen.add(cid);
          result.push(cid);
        }
      }
    } else if (!DEFAULT_COMBOS[id] && !seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  }
  for (const id of Object.keys(ALL_COLUMN_DEFS)) {
    if (!DEFAULT_COMBOS[id] && !seen.has(id)) result.push(id);
  }
  return result;
}

function loadOrderFromStorage(combos: Record<string, ComboConfig>): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_ORDER);
    if (!stored) return getDefaultAtomicOrder();
    const parsed = JSON.parse(stored) as unknown;
    if (!Array.isArray(parsed)) return getDefaultAtomicOrder();
    const result: string[] = [];
    const seen = new Set<string>();
    for (const id of parsed as string[]) {
      if (typeof id !== 'string') continue;
      const childIds = combos[id]?.childIds ?? DEFAULT_COMBOS[id]?.childIds;
      if (childIds?.length) {
        for (const cid of childIds) {
          if (!seen.has(cid) && (ALL_COLUMN_DEFS[cid] || DEFAULT_COMBOS[cid])) {
            seen.add(cid);
            result.push(cid);
          }
        }
      } else if (!DEFAULT_COMBOS[id] && !seen.has(id) && (ALL_COLUMN_DEFS[id] || combos[id])) {
        seen.add(id);
        result.push(id);
      }
    }
    const defaultOrder = getDefaultAtomicOrder();
    for (const id of defaultOrder) {
      if (!seen.has(id)) result.push(id);
    }
    return result.length > 0 ? result : getDefaultAtomicOrder();
  } catch {
    return getDefaultAtomicOrder();
  }
}

function loadColumnsFromStorage(): {
  columnOrder: string[];
  combos: Record<string, ComboConfig>;
  columnWidths: Record<string, string>;
} {
  const combos: Record<string, ComboConfig> = { ...DEFAULT_COMBOS };
  try {
    let stored = localStorage.getItem(STORAGE_KEY_COLUMNS);
    const fromOld = !stored;
    if (!stored) stored = localStorage.getItem(STORAGE_KEY_PROJECTS_OLD);
    if (!stored) {
      const filteredCombos: Record<string, ComboConfig> = {};
      for (const id of DEFAULT_COLUMN_ORDER) {
        if (combos[id]) filteredCombos[id] = combos[id];
      }
      return { columnOrder: [...DEFAULT_COLUMN_ORDER], combos: filteredCombos, columnWidths: {} };
    }
    const parsed = JSON.parse(stored) as Record<string, unknown>;
    if (typeof parsed !== 'object' || parsed === null) {
      return { columnOrder: [...DEFAULT_COLUMN_ORDER], combos, columnWidths: {} };
    }
    const order = parsed.columnOrder as string[] | undefined;
    const widths = (parsed.columnWidths as Record<string, string>) ?? {};
    let storedCombos = parsed.combos as Record<string, ComboConfig> | undefined;
    if (fromOld && !storedCombos) {
      try {
        const combosRaw = localStorage.getItem(STORAGE_KEY_COMBOS_OLD);
        if (combosRaw) storedCombos = JSON.parse(combosRaw) as Record<string, ComboConfig>;
      } catch {
        // ignore
      }
    }
    if (storedCombos && typeof storedCombos === 'object') {
      for (const [key, val] of Object.entries(storedCombos)) {
        if (val && typeof val === 'object' && 'childIds' in val && Array.isArray((val as ComboConfig).childIds)) {
          const c = val as ComboConfig;
          if (c.childIds.length > 0) combos[key] = { childIds: c.childIds, title: c.title ?? 'Комбо' };
        }
      }
    }
    const columnOrder: string[] = [];
    const seen = new Set<string>();
    const isKnown = (id: string) => ALL_COLUMN_DEFS[id] || DEFAULT_COMBOS[id] || combos[id];
    for (const id of order ?? DEFAULT_COLUMN_ORDER) {
      if (!isKnown(id) || seen.has(id)) continue;
      seen.add(id);
      columnOrder.push(id);
    }
    const filteredCombos: Record<string, ComboConfig> = {};
    for (const id of columnOrder) {
      if (combos[id]) filteredCombos[id] = combos[id];
    }
    return { columnOrder, combos: filteredCombos, columnWidths: widths };
  } catch {
    const filteredCombos: Record<string, ComboConfig> = {};
    for (const id of DEFAULT_COLUMN_ORDER) {
      if (combos[id]) filteredCombos[id] = combos[id];
    }
    return { columnOrder: [...DEFAULT_COLUMN_ORDER], combos: filteredCombos, columnWidths: {} };
  }
}

function migrateFromOldStorage(): {
  atomicOrder: string[];
  columnOrder: string[];
  combos: Record<string, ComboConfig>;
  columnWidths: Record<string, string>;
} | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PROJECTS_OLD);
    const combosStored = localStorage.getItem(STORAGE_KEY_COMBOS_OLD);
    if (!stored && !combosStored) return null;
    const combos: Record<string, ComboConfig> = { ...DEFAULT_COMBOS };
    if (combosStored) {
      const parsed = JSON.parse(combosStored) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        for (const [key, val] of Object.entries(parsed)) {
          if (val && typeof val === 'object' && 'childIds' in val && Array.isArray((val as ComboConfig).childIds)) {
            const c = val as ComboConfig;
            if (c.childIds.length > 0) combos[key] = { childIds: c.childIds, title: c.title ?? 'Комбо' };
          }
        }
      }
    }
    let columnOrder: string[] = [];
    const columnWidths: Record<string, string> = {};
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, unknown> | unknown[];
      if (Array.isArray(parsed)) {
        for (const p of parsed as { id: string; width?: string }[]) {
          if (p?.id && ALL_COLUMN_DEFS[p.id]) {
            columnOrder.push(p.id);
            if (p.width) columnWidths[p.id] = p.width;
          }
        }
      } else if (parsed && typeof parsed === 'object') {
        const state = parsed as { columnOrder?: string[]; columnWidths?: Record<string, string> };
        columnOrder = state.columnOrder ?? DEFAULT_COLUMN_ORDER;
        Object.assign(columnWidths, state.columnWidths ?? {});
      }
    }
    if (columnOrder.length === 0) columnOrder = [...DEFAULT_COLUMN_ORDER];
    const filteredCombos: Record<string, ComboConfig> = {};
    for (const id of columnOrder) {
      if (combos[id]) filteredCombos[id] = combos[id];
    }
    const atomicOrder = columnOrder.flatMap((id) =>
      filteredCombos[id]?.childIds?.length ? filteredCombos[id].childIds : [id]
    );
    const seen = new Set(atomicOrder);
    for (const id of getDefaultAtomicOrder()) {
      if (!seen.has(id)) {
        atomicOrder.push(id);
        seen.add(id);
      }
    }
    return { atomicOrder, columnOrder, combos: filteredCombos, columnWidths };
  } catch {
    return null;
  }
}

function loadFromStorage(): {
  atomicOrder: string[];
  columnOrder: string[];
  columnWidths: Record<string, string>;
  combos: Record<string, ComboConfig>;
} {
  applyProjectTableLayoutSchemaMigration();
  const migrated = migrateFromOldStorage();
  if (migrated) {
    const atomicOrder = loadOrderFromStorage(migrated.combos);
    const next = {
      ...migrated,
      atomicOrder,
    };
    try {
      localStorage.setItem(STORAGE_KEY_ORDER, JSON.stringify(next.atomicOrder));
      localStorage.setItem(
        STORAGE_KEY_COLUMNS,
        JSON.stringify({ columnOrder: next.columnOrder, combos: next.combos, columnWidths: next.columnWidths })
      );
    } catch {
      // ignore
    }
    return next;
  }
  const columnsData = loadColumnsFromStorage();
  const atomicOrder = loadOrderFromStorage(columnsData.combos);
  return {
    atomicOrder,
    columnOrder: columnsData.columnOrder,
    columnWidths: columnsData.columnWidths,
    combos: columnsData.combos,
  };
}

function saveToStorage(
  atomicOrder: string[],
  columnOrder: string[],
  columnWidths: Record<string, string>,
  combos: Record<string, ComboConfig>
): void {
  try {
    localStorage.setItem(STORAGE_KEY_ORDER, JSON.stringify(atomicOrder));
    localStorage.setItem(
      STORAGE_KEY_COLUMNS,
      JSON.stringify({ columnOrder, combos, columnWidths })
    );
  } catch {
    // ignore
  }
}

const DEFAULT_COMBO_WIDTH = '200px';

function expandToAtomic(order: string[], combos: Record<string, ComboConfig>): string[] {
  return order.flatMap((id) => (combos[id]?.childIds?.length ? combos[id].childIds : [id]));
}

export interface ProjectsTableCompactModeOptions {
  compactReaders?: boolean;
  compactStudio?: boolean;
  hideColumnIds?: readonly string[];
}

/** Ширины столбцов при включённом компактном режиме (настраиваемая ширина через меню шапки). */
export const READERS_COLUMN_WIDTH_COMPACT = '180px';
export const STUDIO_COLUMN_WIDTH_COMPACT = '220px';

type TableLayoutState = {
  atomicOrder: string[];
  columnOrder: string[];
  columnWidths: Record<string, string>;
  combos: Record<string, ComboConfig>;
};

/** Разобрать все комбо, содержащие атомарный столбец (например при выключении компактного режима). */
function expandCombosContainingInState(prev: TableLayoutState, atomicColumnId: string): TableLayoutState {
  const columnOrder = [...prev.columnOrder];
  const newCombos = { ...prev.combos };
  let changed = false;
  const comboIds = Object.keys(newCombos).filter((cid) => newCombos[cid].childIds.includes(atomicColumnId));
  for (const comboId of comboIds) {
    const combo = newCombos[comboId];
    if (!combo) continue;
    const idx = columnOrder.indexOf(comboId);
    if (idx < 0) continue;
    changed = true;
    columnOrder.splice(idx, 1, ...combo.childIds);
    delete newCombos[comboId];
  }
  if (!changed) return prev;
  return { ...prev, columnOrder, combos: newCombos };
}

const EMPTY_HIDE_COLUMN_IDS: readonly string[] = [];

export function useProjectsTableColumns(compactMode?: ProjectsTableCompactModeOptions) {
  const compactReaders = compactMode?.compactReaders ?? false;
  const compactStudio = compactMode?.compactStudio ?? false;
  const hideColumnIds = compactMode?.hideColumnIds ?? EMPTY_HIDE_COLUMN_IDS;
  const hideColumnIdSet = useMemo(() => new Set(hideColumnIds), [hideColumnIds]);
  const noComboIds = useMemo(
    () => getNoComboColumnIds(compactReaders, compactStudio),
    [compactReaders, compactStudio]
  );

  const [state, setState] = useState(loadFromStorage);
  const { atomicOrder, columnOrder, columnWidths, combos } = state;

  useEffect(() => {
    saveToStorage(atomicOrder, columnOrder, columnWidths, combos);
  }, [atomicOrder, columnOrder, columnWidths, combos]);

  useEffect(() => {
    setState((prev) => {
      let next: TableLayoutState = prev;
      if (!compactReaders) next = expandCombosContainingInState(next, 'readers');
      if (!compactStudio) next = expandCombosContainingInState(next, 'studio');
      return next === prev ? prev : next;
    });
  }, [compactReaders, compactStudio]);

  const childIdToComboId = useMemo(() => {
    const m = new Map<string, string>();
    for (const [comboId, config] of Object.entries(combos)) {
      config.childIds.forEach((c) => m.set(c, comboId));
    }
    return m;
  }, [combos]);

  const visibleAtomics = useMemo(
    () => new Set(expandToAtomic(columnOrder, combos)),
    [columnOrder, combos]
  );

  const columns: TableColumn[] = useMemo(
    () =>
      columnOrder
        .filter((id) => !hideColumnIdSet.has(id))
        .map((id) => {
        const def = ALL_COLUMN_DEFS[id];
        const combo = combos[id];
        const title = combo?.title ?? def?.title ?? id;
        const width = columnWidths[id] ?? def?.width ?? (combo ? DEFAULT_COMBO_WIDTH : '120px');
        return { id, title, width, visible: true, inCombo: null };
      }),
    [columnOrder, columnWidths, combos, hideColumnIdSet]
  );

  const visibleColumns = useMemo(() => columns.filter((c) => c.visible), [columns]);

  const allColumns: TableColumn[] = useMemo(
    () =>
      Object.entries(ALL_COLUMN_DEFS)
        .filter(([id]) => !DEFAULT_COMBOS[id] && !hideColumnIdSet.has(id))
        .map(([id, def]) => {
          const title = def.title;
          const width = columnWidths[id] ?? def.width ?? '120px';
          const inComboId = childIdToComboId.get(id) ?? null;
          const visible = visibleAtomics.has(id);
          return { id, title, width, visible, inCombo: inComboId };
        }),
    [columnWidths, childIdToComboId, visibleAtomics, hideColumnIdSet]
  );

  const gridTemplateColumns = useMemo(
    () => visibleColumns.map((c) => c.width).join(' '),
    [visibleColumns]
  );

  const toggleColumnVisibility = useCallback((id: string) => {
    setState((prev) => {
      const visibleAtomics = new Set(expandToAtomic(prev.columnOrder, prev.combos));
      const currentlyEnabled = visibleAtomics.has(id);

      if (currentlyEnabled) {
        const enabledCount = visibleAtomics.size;
        if (enabledCount <= 1) return prev;

        const containingComboEntry = Object.entries(prev.combos).find(([, c]) => c.childIds.includes(id));

        if (containingComboEntry) {
          const [comboId, combo] = containingComboEntry;
          const newChildIds = combo.childIds.filter((c) => c !== id);
          const newCombos = { ...prev.combos };
          const comboIdx = prev.columnOrder.indexOf(comboId);
          const nextOrder = [...prev.columnOrder];

          if (newChildIds.length < 2) {
            delete newCombos[comboId];
            if (comboIdx >= 0) {
              nextOrder.splice(comboIdx, 1, ...newChildIds);
            } else {
              nextOrder.push(...newChildIds);
            }
          } else {
            newCombos[comboId] = { ...combo, childIds: newChildIds };
          }

          return { ...prev, columnOrder: nextOrder, combos: newCombos };
        }

        return {
          ...prev,
          columnOrder: prev.columnOrder.filter((cid) => cid !== id),
        };
      }

      const insertIdx = prev.columnOrder.findIndex((cid) => {
        const atoms = prev.combos[cid]?.childIds ?? [cid];
        return atoms.some((aid) => prev.atomicOrder.indexOf(aid) > prev.atomicOrder.indexOf(id));
      });
      const nextOrder = [...prev.columnOrder];
      nextOrder.splice(insertIdx === -1 ? nextOrder.length : insertIdx, 0, id);
      const nextAtomic =
        prev.atomicOrder.includes(id) ? prev.atomicOrder : [...prev.atomicOrder, id];
      return { ...prev, columnOrder: nextOrder, atomicOrder: nextAtomic };
    });
  }, []);

  const moveColumn = useCallback((fromIndex: number, toIndex: number) => {
    setState((prev) => {
      const order = [...prev.columnOrder];
      if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= order.length || toIndex >= order.length) return prev;
      const [removed] = order.splice(fromIndex, 1);
      order.splice(toIndex, 0, removed);

      const fromAtoms = prev.combos[removed]?.childIds ?? [removed];
      const toId = order[toIndex + 1] ?? order[toIndex];
      const toAtoms = prev.combos[toId]?.childIds ?? [toId];
      const firstToAtom = toAtoms[0];

      const nextAtomic = [...prev.atomicOrder];
      for (const aid of fromAtoms) {
        const idx = nextAtomic.indexOf(aid);
        if (idx >= 0) nextAtomic.splice(idx, 1);
      }
      const insertPos = nextAtomic.indexOf(firstToAtom);
      if (insertPos >= 0) {
        nextAtomic.splice(insertPos, 0, ...fromAtoms);
      } else {
        nextAtomic.push(...fromAtoms);
      }

      return { ...prev, columnOrder: order, atomicOrder: nextAtomic };
    });
  }, []);

  const setColumnWidth = useCallback((columnId: string, width: string) => {
    setState((prev) => ({
      ...prev,
      columnWidths: { ...prev.columnWidths, [columnId]: width },
    }));
  }, []);

  const clearColumnWidths = useCallback((ids: string[]) => {
    setState((prev) => {
      const next = { ...prev.columnWidths };
      for (const id of ids) delete next[id];
      return { ...prev, columnWidths: next };
    });
  }, []);

  const setComboTitle = useCallback((comboId: string, title: string) => {
    setState((prev) => {
      const combo = prev.combos[comboId];
      if (!combo) return prev;
      return {
        ...prev,
        combos: { ...prev.combos, [comboId]: { ...combo, title } },
      };
    });
  }, []);

  const setComboChildren = useCallback((comboId: string, childIds: string[]) => {
    setState((prev) => {
      const combo = prev.combos[comboId];
      if (!combo) return prev;
      return {
        ...prev,
        combos: { ...prev.combos, [comboId]: { ...combo, childIds } },
      };
    });
  }, []);

  const toggleComboColumn = useCallback((comboId: string, columnId: string, add: boolean) => {
    setState((prev) => {
      const combo = prev.combos[comboId];
      if (!combo) return prev;
      const isButtonsCombo = combo.childIds.some((c) => BUTTON_COLUMNS_GROUP.has(c));
      if (add && isButtonsCombo && !BUTTON_COLUMNS_GROUP.has(columnId)) return prev;
      if (add && !isButtonsCombo && BUTTON_COLUMNS_GROUP.has(columnId)) return prev;
      let childIds = [...combo.childIds];
      if (add) {
        if (!childIds.includes(columnId) && !noComboIds.has(columnId)) {
          childIds = [...childIds, columnId];
        }
      } else {
        childIds = childIds.filter((c) => c !== columnId);
      }
      if (childIds.length === 0) return prev;
      return { ...prev, combos: { ...prev.combos, [comboId]: { ...combo, childIds } } };
    });
  }, [noComboIds]);

  const getComboableColumns = useCallback(() => {
    const ids = Object.keys(ALL_COLUMN_DEFS).filter((id) => !noComboIds.has(id));
    return ids.map((id) => ({ id, title: ALL_COLUMN_DEFS[id]?.title ?? id }));
  }, [noComboIds]);

  /** Столбцы, которые можно добавить в комбо при клике на anchorColumnId */
  const getComboableColumnsForAnchor = useCallback((anchorColumnId: string) => {
    const anchorCombo = combos[anchorColumnId];
    const isButtonsContext =
      anchorColumnId === 'menu_combo' ||
      anchorColumnId === 'status_flags_combo' ||
      BUTTON_COLUMNS_GROUP.has(anchorColumnId) ||
      combos.menu_combo?.childIds.includes(anchorColumnId) ||
      combos.status_flags_combo?.childIds.includes(anchorColumnId) ||
      (anchorCombo?.childIds?.length !== undefined &&
        anchorCombo.childIds.every((id) => BUTTON_COLUMNS_GROUP.has(id)));
    if (isButtonsContext) {
      return Array.from(BUTTON_COLUMNS_GROUP)
        .filter((id) => !hideColumnIdSet.has(id))
        .map((id) => ({
          id,
          title: (MENU_COLUMN_LABELS[id] ?? ALL_COLUMN_DEFS[id]?.title) || id,
        }));
    }
    return getComboableColumns().filter((c) => !BUTTON_COLUMNS_GROUP.has(c.id));
  }, [combos, getComboableColumns, hideColumnIdSet]);

  /** Разобрать комбо — заменить комбо-столбец на атомарные и удалить из combos */
  const expandCombo = useCallback((comboId: string) => {
    setState((prev) => {
      const combo = prev.combos[comboId];
      if (!combo) return prev;
      const idx = prev.columnOrder.indexOf(comboId);
      if (idx < 0) return prev;
      const next = [...prev.columnOrder];
      next.splice(idx, 1, ...combo.childIds);
      const newCombos = { ...prev.combos };
      delete newCombos[comboId];
      return { ...prev, columnOrder: next, combos: newCombos };
    });
  }, []);

  /** Вернуть по умолчанию: порядок, комбо, порядок атомарных полей и пользовательские ширины */
  const resetColumnOrderToDefault = useCallback(() => {
    setState((prev) => {
      const filteredCombos: Record<string, ComboConfig> = {};
      for (const id of DEFAULT_COLUMN_ORDER) {
        if (DEFAULT_COMBOS[id]) filteredCombos[id] = DEFAULT_COMBOS[id];
      }
      return {
        ...prev,
        columnOrder: [...DEFAULT_COLUMN_ORDER],
        combos: filteredCombos,
        atomicOrder: getDefaultAtomicOrder(),
        columnWidths: {},
      };
    });
  }, []);

  /** Разобрать все комбо — заменить каждый комбо-столбец на атомарные */
  const expandAllCombos = useCallback(() => {
    setState((prev) => {
      const nextOrder = prev.columnOrder.flatMap((id) =>
        prev.combos[id]?.childIds?.length ? prev.combos[id].childIds : [id]
      );
      return { ...prev, columnOrder: nextOrder, combos: {} };
    });
  }, []);

  /** Собрать в комбо — заменить атомарные столбцы на комбо */
  const collapseToCombo = useCallback((comboId: string) => {
    setState((prev) => {
      const combo = prev.combos[comboId] ?? DEFAULT_COMBOS[comboId];
      if (!combo) return prev;
      const order = prev.columnOrder;
      const firstIdx = order.findIndex((id) => id === combo.childIds[0]);
      if (firstIdx < 0) return prev;
      const slice = order.slice(firstIdx, firstIdx + combo.childIds.length);
      const matches = combo.childIds.every((cid, i) => slice[i] === cid);
      if (!matches) return prev;
      const next = [...order];
      next.splice(firstIdx, combo.childIds.length, comboId);
      const newCombos = { ...prev.combos, [comboId]: combo };
      return { ...prev, columnOrder: next, combos: newCombos };
    });
  }, []);

  const isComboExpanded = useCallback((comboId: string) => {
    const combo = combos[comboId];
    if (!combo) return false;
    return !columnOrder.includes(comboId);
  }, [combos, columnOrder]);

  /** Найти комбо, содержащее столбец (или null если столбец атомарный). combos содержит только активные комбо. */
  const getComboContainingColumn = useCallback((columnId: string): string | null => {
    for (const [comboId, config] of Object.entries(combos)) {
      if (config.childIds.includes(columnId)) return comboId;
    }
    return null;
  }, [combos]);

  /** Получить столбцы в комбо для данного столбца или комбо (или [columnId] если атомарный). */
  const getComboColumnsFor = useCallback((columnId: string): string[] => {
    const combo = combos[columnId];
    if (combo?.childIds?.length) return [...combo.childIds];
    const comboId = getComboContainingColumn(columnId);
    if (comboId) return [...combos[comboId].childIds];
    return [columnId];
  }, [combos, getComboContainingColumn]);

  /** Установить выбор комбо: selectedIds — столбцы в комбо. Схлопывает или разбирает. */
  const setComboSelection = useCallback((anchorColumnId: string, selectedIds: string[]) => {
    if (noComboIds.has(anchorColumnId)) return;
    const anchorCombo = combos[anchorColumnId];
    const isButtonsContext =
      anchorColumnId === 'menu_combo' ||
      anchorColumnId === 'status_flags_combo' ||
      BUTTON_COLUMNS_GROUP.has(anchorColumnId) ||
      combos.menu_combo?.childIds.includes(anchorColumnId) ||
      combos.status_flags_combo?.childIds.includes(anchorColumnId) ||
      (anchorCombo?.childIds?.length !== undefined &&
        anchorCombo.childIds.every((id) => BUTTON_COLUMNS_GROUP.has(id)));
    const filtered = isButtonsContext
      ? selectedIds.filter((id) => BUTTON_COLUMNS_GROUP.has(id))
      : selectedIds.filter((id) => !BUTTON_COLUMNS_GROUP.has(id));
    if (filtered.length === 0 && selectedIds.length > 0) return;
    const idsBase = filtered.length > 0 ? filtered : selectedIds;
    const ids = idsBase.filter((id) => !noComboIds.has(id));
    if (ids.length === 0 && idsBase.length > 0) return;
    setState((prev) => {
      const comboId = prev.combos[anchorColumnId]
        ? anchorColumnId
        : Object.entries(prev.combos).find(([, c]) => c.childIds.includes(anchorColumnId))?.[0] ?? null;
      const sorted = [...ids].sort();
      const order = [...prev.columnOrder];

      if (ids.length >= 2) {
        const predefined = Object.entries(DEFAULT_COMBOS).find(
          ([_, c]) => c.childIds.length === sorted.length && [...c.childIds].sort().every((id, i) => id === sorted[i])
        )?.[0];
        const existingComboId = predefined ?? Object.entries(prev.combos).find(
          ([_, c]) => c.childIds.length === sorted.length && [...c.childIds].sort().every((id, i) => id === sorted[i])
        )?.[0];
        const newComboId = existingComboId ?? `combo_${sorted.join('_')}`;

        const originalTitle = existingComboId
          ? (DEFAULT_COMBOS[existingComboId]?.title ?? prev.combos[existingComboId]?.title ?? 'Комбо')
          : (comboId ? prev.combos[comboId]?.title ?? 'Комбо' : 'Комбо');
        const newCombo: ComboConfig = {
          childIds: sorted,
          title: prev.combos[newComboId]?.title ?? originalTitle,
        };

        const removedChildren = comboId
          ? (prev.combos[comboId]?.childIds ?? []).filter((c) => !ids.includes(c))
          : [];

        const idsBeingAddedToCombo = comboId
          ? ids.filter((id) => !(prev.combos[comboId]?.childIds ?? []).includes(id))
          : [];
        let toRemove: string[];
        if (comboId) {
          toRemove = [comboId, ...idsBeingAddedToCombo];
        } else {
          const standaloneInOrder = ids.filter((id) => order.includes(id));
          const combosToMerge = Object.entries(prev.combos)
            .filter(([, c]) => c.childIds.some((cid) => ids.includes(cid)))
            .map(([cid]) => cid);
          toRemove = [...standaloneInOrder, ...combosToMerge];
        }
        const insertIdxOrig = order.findIndex((id) => toRemove.includes(id));
        if (insertIdxOrig < 0) return prev;
        const next = order.filter((id) => !toRemove.includes(id));
        const insertIdx = Math.min(insertIdxOrig, next.length);
        next.splice(insertIdx, 0, newComboId, ...removedChildren);

        const newCombos = { ...prev.combos, [newComboId]: newCombo };
        if (comboId && comboId !== newComboId) delete newCombos[comboId];

        return {
          ...prev,
          columnOrder: next,
          combos: newCombos,
        };
      }
      if (ids.length === 1) {
        if (comboId) {
          const idx = order.indexOf(comboId);
          if (idx < 0) return prev;
          const combo = prev.combos[comboId];
          const childIdsInOrder = combo?.childIds ?? [];
          const next = [...order];
          next.splice(idx, 1, ...childIdsInOrder);
          const newCombos = { ...prev.combos };
          delete newCombos[comboId];
          return { ...prev, columnOrder: next, combos: newCombos };
        }
        return prev;
      }
      return prev;
    });
  }, [combos, noComboIds]);

  const toggleComboSelection = useCallback((anchorColumnId: string, columnId: string) => {
    if (noComboIds.has(columnId)) return;
    const anchorCombo = combos[anchorColumnId];
    const isButtonsContext =
      anchorColumnId === 'menu_combo' ||
      anchorColumnId === 'status_flags_combo' ||
      BUTTON_COLUMNS_GROUP.has(anchorColumnId) ||
      combos.menu_combo?.childIds.includes(anchorColumnId) ||
      combos.status_flags_combo?.childIds.includes(anchorColumnId) ||
      (anchorCombo?.childIds?.length !== undefined &&
        anchorCombo.childIds.every((id) => BUTTON_COLUMNS_GROUP.has(id)));
    if (isButtonsContext && !BUTTON_COLUMNS_GROUP.has(columnId)) return;
    if (!isButtonsContext && BUTTON_COLUMNS_GROUP.has(columnId)) return;
    const current = getComboColumnsFor(anchorColumnId);
    const isIn = current.includes(columnId);
    let next = isIn
      ? (current.length > 1 ? current.filter((id) => id !== columnId) : current)
      : [...current, columnId];
    if (next.length === 0) next = [anchorColumnId];
    setComboSelection(anchorColumnId, next);
  }, [combos, getComboColumnsFor, setComboSelection, noComboIds]);

  const visibility: Record<string, boolean> = useMemo(() => {
    const v: Record<string, boolean> = {};
    for (const id of Object.keys(ALL_COLUMN_DEFS)) {
      if (!DEFAULT_COMBOS[id]) v[id] = visibleAtomics.has(id);
    }
    return v;
  }, [visibleAtomics]);

  return {
    columns,
    allColumns,
    visibleColumns,
    gridTemplateColumns,
    combos,
    columnOrder,
    atomicOrder,
    toggleColumnVisibility,
    moveColumn,
    setColumnWidth,
    clearColumnWidths,
    setComboTitle,
    setComboChildren,
    toggleComboColumn,
    getComboableColumns,
    expandCombo,
    collapseToCombo,
    isComboExpanded,
    getComboContainingColumn,
    getComboColumnsFor,
    setComboSelection,
    toggleComboSelection,
    getComboableColumnsForAnchor,
    resetColumnOrderToDefault,
    expandAllCombos,
    visibility,
  };
}

export { ALL_COLUMN_DEFS, DEFAULT_COMBO_WIDTH };
